import React, { useState, useEffect, useRef, useContext } from 'react';
import { ContextMenu } from 'primereact/contextmenu';
import { confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import NewTopic from "../topic/NewTopic"
import NewCard from "../card/NewCard"

import axios from 'axios';
import { UserContext } from '../../hooks/UserContext';
import EditTopic from '../topic/EditTopic';

// A course check's open findings on a topic or card (CONTEXT.md "Check").
function OpenBadge({ count }) {
  if (!count) return null;
  return (
    <span className="sidebar-badge-open" title={`${count} open finding${count === 1 ? '' : 's'}`}>
      {count}
    </span>
  );
}

const CourseTree = ({
  selectedCourse, selectedTopic, selectedCard, onTopicSelect, onCardSelect, canEdit = true, openFindings = [],
}) => {
  const { user } = useContext(UserContext);
  const [course, setCourse] = useState();
  const [nodes, setNodes] = useState([]);
  const [selectedNodeKey, setSelectedNodeKey] = useState(null);
  const [filterText, setFilterText] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const toast = useRef(null);
  const cm = useRef(null);
  const cmt = useRef(null);
  const editTopicRef = useRef();
  const newTopicRef = useRef();
  const newCardRef = useRef();

  useEffect(() => {
    setCourse(selectedCourse);
  }, [selectedCourse]);

  // A topic picked from outside the tree (the review panel's next card, a
  // linked card) becomes the highlighted one.
  useEffect(() => {
    if (!selectedTopic?.id) return;
    setSelectedNodeKey(key => (String(key).split('-')[0] === String(selectedTopic.id) ? key : selectedTopic.id));
  }, [selectedTopic?.id]);

  const openByCard = Object.fromEntries(openFindings.map(c => [c.card_id, c.open]));
  const topicOpen = node => (node.children || []).reduce((sum, card) => sum + (openByCard[card.cardId] || 0), 0);

  useEffect(() => {
    async function fetchTreeData() {
      if (course && course.id) {
        const res = await getTopics(course.id);
        const sortedTopics = sortItemsByPosition(res.data.items);
        const topicIds = Object.values(sortedTopics).map(x => x.id);

        Promise.all(topicIds.map(id => getCards(id))).then(async (values) => {
          let sortedCards = [];
          for (const cards of values) {
            sortedCards.push(sortItemsByPosition(cards));
          }

          const options = sortedTopics.map((d, index) => ({
            "key": d.id,
            "label": d.name,
            "data": d.name,
            "type": d.type,
            "position_id": d.position_id,
            "children": sortedCards[index].map(value => ({
              "key": `${d.id}-${value.id}`,
              "label": `${value.position_id}. card${value.id}`,
              "data": value.body,
              "position_id": value.position_id,
              "cardId": value.id
            }))
          }));
          setNodes(options);

          // Auto-select first topic (the editor declines when a card is
          // already chosen or being restored)
          if (options.length > 0) {
            const firstTopic = options[0];
            let didSelect = true;
            if (firstTopic.children && firstTopic.children.length > 0) {
              const firstCard = firstTopic.children[0];
              const [topicId, cardId] = firstCard.key.split('-');
              didSelect = await onCardSelect(cardId, topicId, { auto: true });
            } else {
              didSelect = await onTopicSelect(firstTopic.key.toString(), { auto: true });
            }
            if (didSelect !== false) {
              setSelectedNodeKey(firstTopic.key);
            }
          }
        });
      }
    }
    fetchTreeData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course]);


  const getTopics = id => axios.get(`/api/courses/${id}/topics`, {
    headers: { 'Authorization': `Bearer ${user.token}`, 'X-EDITOR': 'nanoms' }
  });

  const getCards = async (id) => {
    const res = await axios.get(`/api/topics/${id}/cards`, {
      headers: { 'Authorization': `Bearer ${user.token}`, 'X-EDITOR': 'nanoms' }
    });
    return res.data.items;
  };

  const deleteCard = id => axios.delete(`/api/cards/${id}`, {
    headers: { 'Authorization': `Bearer ${user.token}`, 'X-EDITOR': 'nanoms' }
  });

  const deleteTopic = id => axios.delete(`/api/topics/${id}`, {
    headers: { 'Authorization': `Bearer ${user.token}`, 'X-EDITOR': 'nanoms' }
  });

  const compareItems = (a, b) => {
    if (a.position_id > b.position_id) return 1;
    else if (b.position_id > a.position_id) return -1;
    else if (a.id > b.id) return 1;
    else return -1;
  };

  const sortItemsByPosition = (itemsArray) => {
    const noPositionItems = itemsArray.filter(item => item.position_id === null);
    noPositionItems.sort(compareItems);
    const itemsWithPosition = itemsArray.filter(item => item.position_id !== null);
    itemsWithPosition.sort(compareItems);
    return itemsWithPosition.concat(noPositionItems);
  };

  const compareCardNodes = (a, b) => {
    const pa = a.position_id ?? Number.MAX_SAFE_INTEGER;
    const pb = b.position_id ?? Number.MAX_SAFE_INTEGER;
    return pa - pb || (a.cardId ?? 0) - (b.cardId ?? 0);
  };

  const addChildNode = (currentTree, nodeAdd) => {
    return currentTree.map((node) => {
      const [parentId] = nodeAdd.key.split('-');
      if (node.key === parseInt(parentId)) {
        const children = [...(node.children || []), nodeAdd].sort(compareCardNodes);
        return { ...node, children };
      }
      return node;
    });
  };

  const addCardNode = (newNodeData) => {
    setNodes(prev => addChildNode(prev, newNodeData));
    const [topicId] = newNodeData.key.split('-');
    const topicNode = nodes.find(node => node.key === parseInt(topicId));
    toast.current.show({
      severity: 'success',
      summary: 'Card added',
      detail: `Card ${newNodeData.cardId} added to "${topicNode ? topicNode.label : `topic ${topicId}`}"`,
      life: 3000,
    });
  };

  const addTopicNode = (newNodeData) => {
    setNodes(prev => [...prev, newNodeData]);
  };

  const updateTopicNode = (newNodeData) => {
    const destNode = nodes.find(node => node.key === newNodeData.key);
    newNodeData.children = destNode ? [...destNode.children] : [];
    setNodes(prev => prev.map(node => node.key === newNodeData.key ? newNodeData : node));
  };

  const contextMenuTopic = [
    {
      label: 'Edit',
      icon: 'pi pi-pencil',
      command: () => { editTopicRef.current.openModal(selectedNodeKey); }
    },
    {
      label: 'Add Card',
      icon: 'pi pi-plus',
      command: () => { if (newCardRef.current) newCardRef.current.openModal(selectedNodeKey); }
    },
    {
      label: 'Delete',
      icon: 'pi pi-trash',
      command: () => { confirmDelete(); }
    }
  ];

  const contextMenu = [
    {
      label: 'Delete',
      icon: 'pi pi-trash',
      command: () => { confirmDelete(); }
    }
  ];

  const confirmDelete = () => {
    confirmDialog({
      message: 'Are you sure you want to delete this item?',
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      accept: () => executeDelete(selectedNodeKey),
    });
  };

  const confirmDeleteKey = (key) => {
    setSelectedNodeKey(key);
    confirmDialog({
      message: 'Are you sure you want to delete this item?',
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      accept: () => executeDelete(key),
    });
  };

  const executeDelete = (key) => {
    if (Number.isInteger(key)) {
      const topicId = parseInt(key);
      deleteTopic(topicId);
      setSelectedNodeKey(null);
      setNodes(prev => prev.filter(node => node.key !== topicId));
      toast.current.show({ severity: 'info', summary: 'Confirmed', detail: 'Topic with id ' + topicId + ' deleted', life: 3000 });
    } else {
      const [topicId, cardId] = key.split('-');
      deleteCard(cardId);
      setSelectedNodeKey(null);
      setNodes(prev => prev.map(node =>
        node.key === parseInt(topicId)
          ? { ...node, children: node.children.filter(child => child.key !== key) }
          : node
      ));
      toast.current.show({ severity: 'info', summary: 'Confirmed', detail: 'Card with id ' + cardId + ' deleted', life: 3000 });
    }
  };

  const handleTopicClick = async (topicNode) => {
    // auto-load first card if available
    let didSelect;
    if (topicNode.children && topicNode.children.length > 0) {
      const firstCard = topicNode.children[0];
      const [topicId, cardId] = firstCard.key.split('-');
      didSelect = await onCardSelect(cardId, topicId);
    } else {
      didSelect = await onTopicSelect(topicNode.key.toString());
    }
    if (didSelect !== false) {
      setSelectedNodeKey(topicNode.key);
    }
  };

  const handleCardClick = async (cardKey) => {
    const [topicId, cardId] = cardKey.split('-');
    const didSelect = await onCardSelect(cardId, topicId);
    if (didSelect !== false) {
      setSelectedNodeKey(cardKey);
    }
  };

  const handleContextMenu = (e, node) => {
    if (!canEdit) return;
    e.preventDefault();
    setSelectedNodeKey(node.key);
    if (node.children !== undefined) {
      cmt.current.show(e);
    } else {
      cm.current.show(e);
    }
  };

  const totalCards = nodes.reduce((sum, n) => sum + (n.children ? n.children.length : 0), 0);

  const filteredNodes = filterText
    ? nodes.filter(n => n.label.toLowerCase().includes(filterText.toLowerCase()))
    : nodes;

  const isTopicSelected = (node) => {
    if (selectedTopic && selectedTopic.id === node.key) return true;
    if (selectedNodeKey === node.key) return true;
    return false;
  };

  const isCardSelected = (cardKey) => {
    if (selectedCard && selectedNodeKey === cardKey) return true;
    // check via selectedCard object
    if (selectedCard) {
      const testKey = `${selectedTopic?.id}-${selectedCard.id}`;
      return testKey === cardKey;
    }
    return false;
  };

  return (
    <div className="sidebar-panel">
      {/* Sidebar header */}
      <div className="sidebar-header">
        <span className="sidebar-title">Topics</span>
        <div className="sidebar-header-actions">
          <button
            className="sidebar-icon-btn"
            title="Filter topics"
            onClick={() => setFilterOpen(!filterOpen)}
          >
            <i className="pi pi-search" />
          </button>
          {canEdit && (
            <button
              className="sidebar-icon-btn"
              title="Add topic"
              onClick={() => { if (newTopicRef.current) newTopicRef.current.openModal(); }}
            >
              <i className="pi pi-plus" />
            </button>
          )}
        </div>
      </div>

      {/* Filter input */}
      {filterOpen && (
        <div className="sidebar-filter">
          <input
            type="text"
            className="sidebar-filter-input"
            placeholder="Filter topics..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            autoFocus
          />
        </div>
      )}

      {/* Topic list */}
      <div className="sidebar-list">
        {filteredNodes.map((node) => {
          const selected = isTopicSelected(node);
          const hasMultiCards = node.children && node.children.length > 1;

          return (
            <div key={node.key} className="sidebar-topic-wrapper">
              <div
                className={`sidebar-topic-row ${selected ? 'sidebar-topic-selected' : ''}`}
                onClick={() => handleTopicClick(node)}
                onContextMenu={(e) => handleContextMenu(e, node)}
              >
                <span className="sidebar-topic-position">{node.position_id ?? '–'}</span>
                <span className="sidebar-topic-name">{node.label}</span>
                {node.type === 'free' && (
                  <span className="sidebar-badge-free">free</span>
                )}
                <OpenBadge count={topicOpen(node)} />
                {canEdit && (
                  <span className="sidebar-topic-menu" onClick={(e) => { e.stopPropagation(); }}>
                    <button
                      className="sidebar-icon-btn sidebar-dots-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedNodeKey(node.key);
                        cmt.current.show(e);
                      }}
                    >
                      <i className="pi pi-ellipsis-v" />
                    </button>
                  </span>
                )}
              </div>

              {/* Card tabs: show only when topic is selected and has >1 cards */}
              {selected && hasMultiCards && (
                <div className="sidebar-card-tabs">
                  {node.children.map((card, idx) => {
                    const cardSelected = isCardSelected(card.key);
                    return (
                      <button
                        key={card.key}
                        className={`sidebar-card-tab ${cardSelected ? 'sidebar-card-tab-active' : ''}`}
                        onClick={() => handleCardClick(card.key)}
                        onContextMenu={(e) => handleContextMenu(e, card)}
                      >
                        Card {idx + 1}
                        <OpenBadge count={openByCard[card.cardId] || 0} />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {filteredNodes.length === 0 && nodes.length > 0 && (
          <div className="sidebar-empty">No topics match filter</div>
        )}
        {nodes.length === 0 && course && course.id && (
          <div className="sidebar-empty">No topics yet</div>
        )}
        {(!course || !course.id) && (
          <div className="sidebar-empty">Select a course</div>
        )}
      </div>

      {/* Footer */}
      <div className="sidebar-footer">
        {nodes.length} topics, {totalCards} cards
      </div>

      {/* Hidden modals and context menus */}
      {canEdit && <ContextMenu model={contextMenu} ref={cm} appendTo={document.body} />}
      {canEdit && <ContextMenu model={contextMenuTopic} ref={cmt} appendTo={document.body} />}
      {canEdit && <EditTopic updateTopicNode={updateTopicNode} ref={editTopicRef} />}
      {canEdit && <NewTopic selectedCourse={selectedCourse} addTopicNode={addTopicNode} ref={newTopicRef} />}
      {canEdit && <NewCard selectedCourse={selectedCourse} selectedTopic={selectedTopic} addCardNode={addCardNode} ref={newCardRef} />}
      <Toast ref={toast} />
    </div>
  );
};

export default CourseTree;
